import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { ShieldCheck, ShieldWarning, Clock, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function WorkerVerification() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    business_name: "",
    license_number: "",
    license_image_url: "",
    insurance_image_url: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/workers/me").then((r) => {
      setProfile(r.data);
      const v = r.data?.verification;
      if (v && v.status !== "unverified") {
        setForm({
          business_name: v.business_name || "",
          license_number: v.license_number || "",
          license_image_url: v.license_image_url || "",
          insurance_image_url: v.insurance_image_url || "",
          notes: v.notes || "",
        });
      }
    }).finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/workers/verification/submit", form);
      toast.success("Submitted for review");
      const fresh = await api.get("/workers/me");
      setProfile(fresh.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  const status = profile?.verification?.status || "unverified";
  const banner = {
    unverified: { Icon: ShieldWarning, color: "border-slate-300 bg-slate-50", text: "text-slate-700", title: "Get verified", desc: "Submit your license and insurance to unlock the Verified Pro badge." },
    pending: { Icon: Clock, color: "border-amber-300 bg-amber-50", text: "text-amber-800", title: "Under review", desc: "We're reviewing your documents. Most decisions within 24 hours." },
    verified: { Icon: CheckCircle, color: "border-emerald-300 bg-emerald-50", text: "text-emerald-800", title: "Verified Pro", desc: "You're a Verified Pro. Your badge appears across CanHireSolution.com." },
    rejected: { Icon: XCircle, color: "border-red-300 bg-red-50", text: "text-red-800", title: "Verification rejected", desc: profile?.verification?.decision_notes || "Please review notes and resubmit with updated documents." },
  }[status];

  if (!profile) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-24 pb-16 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Verification</div>
          <h1 className="font-display text-4xl mb-3">Complete your profile first</h1>
          <p className="text-slate-600 mb-6">You need a worker profile before submitting verification documents.</p>
          <button onClick={() => navigate("/worker/onboarding")} className="bg-brand text-white px-5 py-3 font-semibold" data-testid="goto-onboarding">Go to onboarding →</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Verification</div>
        <h1 className="font-display text-4xl mb-2 flex items-center gap-3">
          <ShieldCheck size={36} weight="fill" className="text-brand" /> Verified Pro
        </h1>
        <p className="text-slate-600 mb-6">Stand out with a license + insurance check. Verified Pros get a badge, priority placement, and convert 30–50% more leads.</p>

        <div className={`border-2 ${banner.color} p-5 mb-8 flex items-start gap-3`}>
          <banner.Icon size={24} className={banner.text + " flex-shrink-0 mt-0.5"} weight="bold" />
          <div>
            <div className={`font-display text-lg ${banner.text}`}>{banner.title}</div>
            <p className="text-sm text-slate-700">{banner.desc}</p>
          </div>
        </div>

        {status !== "verified" && (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">Business / Legal name</label>
              <input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5" data-testid="verif-business-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">Trade license number</label>
              <input required value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5 font-mono text-sm" data-testid="verif-license-number" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">License image URL</label>
              <input required type="url" value={form.license_image_url} onChange={(e) => setForm({ ...form, license_image_url: e.target.value })} placeholder="https://…" className="w-full border border-slate-300 px-3 py-2.5" data-testid="verif-license-img" />
              <p className="text-xs text-slate-500 mt-1">Upload your license to any image host and paste the URL. File upload coming soon.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">Insurance image URL <span className="lowercase font-normal">(optional)</span></label>
              <input type="url" value={form.insurance_image_url} onChange={(e) => setForm({ ...form, insurance_image_url: e.target.value })} placeholder="https://…" className="w-full border border-slate-300 px-3 py-2.5" data-testid="verif-insurance-img" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 block mb-1">Notes for reviewer (optional)</label>
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-slate-300 px-3 py-2.5" data-testid="verif-notes" />
            </div>
            <button type="submit" disabled={busy} className="bg-brand text-white px-6 py-3 font-semibold disabled:opacity-50" data-testid="verif-submit-btn">
              {busy ? "Submitting…" : status === "pending" ? "Resubmit" : status === "rejected" ? "Resubmit" : "Submit for review"}
            </button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
